import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import pluralize from 'pluralize'
import Fuse from 'fuse.js'
import { get } from 'lodash'
import classNames from 'classnames'

import { Checkbox } from '../../../ui/checkbox'
import { Input } from '../../../ui/input'
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner'
import { hasDistinctStudentLegalName, studentDisplayName } from '../../../../utilities/studentNames'

const fuzzySearchOptions = {
  findAllMatches: true,
  threshold: 0.4,
  keys: ['preferredName', 'fullName']
}

function MultiSelectStudent({
  className,
  value: students,
  onChange,
  loading,
  label,
  helpText,
  errorMessage,
  required
}) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(
    new Set(students.filter((student) => student.selected).map((student) => student.id))
  )

  useEffect(() => {
    setSelected(new Set(students.filter((student) => student.selected).map((student) => student.id)))
  }, [students])

  function toggle(student) {
    let newSelectedStudents
    if (selected.has(student.id)) {
      selected.delete(student.id)
      newSelectedStudents = new Set(selected)
    } else {
      newSelectedStudents = new Set(selected.add(student.id))
    }
    setSelected(newSelectedStudents)
    onChange(
      students.map((student) => {
        return {
          ...student,
          selected: newSelectedStudents.has(student.id)
        }
      })
    )
  }

  function filteredStudents() {
    let results = students

    if (query) {
      const fuzzySearch = new Fuse(students, fuzzySearchOptions)
      results = fuzzySearch.search(query).map(({ item }) => item)
    }

    return results.slice().sort((a, b) => {
      const aSelected = selected.has(a.id)
      const bSelected = selected.has(b.id)

      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1
      }

      return studentDisplayName(a).localeCompare(studentDisplayName(b), 'en', { sensitivity: 'base' })
    })
  }

  return (
    <section className={className} data-cy="students-multi-select">
      {label && (
        <label
          className={`${classNames({
            'border-red-500 text-red-500': errorMessage,
            'text-gray-700': !errorMessage
          })} block text-sm font-medium mb-3`}
        >
          {label}
          {required && '*'}
        </label>
      )}
      {helpText && <p className="mb-2 text-xs text-gray-500">{helpText}</p>}
      <div className="flex items-center justify-between mb-3">
        <p
          className={`text-sm font-medium ${classNames({
            'text-gray-700': !errorMessage,
            'text-red-500': errorMessage
          })}`}
        >
          {!loading && `${pluralize('student', selected.size, true)} enrolled`}
        </p>
      </div>
      <div
        className={`${classNames({
          'border-gray-200': !errorMessage,
          'border-red-300': errorMessage
        })} h-96 overflow-y-auto rounded-lg border-2 bg-white shadow-sm`}
      >
        {loading ? (
          <LoadingSpinner size="8" className="flex items-center justify-center my-10" />
        ) : (
          <>
            <div className="sticky top-0 bg-white border-b border-gray-100 p-3 z-10">
              <Input
                id="student-search"
                type="search"
                placeholder="Search for students by name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <ul className="divide-y divide-gray-100">
              {filteredStudents().map((student) => {
                const isSelected = selected.has(student.id)
                return (
                  <li
                    key={student.id}
                    className={`flex items-center px-4 py-3 transition-colors cursor-pointer ${
                      isSelected ? 'bg-purple-50 hover:bg-purple-100' : 'hover:bg-gray-50'
                    }`}
                    data-cy="student-option"
                    onClick={() => toggle(student)}
                  >
                    <div className="flex items-center justify-between w-full gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => {}}
                          className="h-5 w-5 flex-shrink-0"
                          data-cy={`${student.fullName}-check-button`}
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{studentDisplayName(student)}</p>
                          {hasDistinctStudentLegalName(student) && (
                            <p className="text-xs text-gray-500 truncate">{student.fullName}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600 flex-shrink-0">
                        {get(student, 'currentSkillLevel.name') && (
                          <span className="px-2 py-1 text-xs font-medium bg-white rounded-md border border-gray-200">
                            {get(student, 'currentSkillLevel.name')}
                          </span>
                        )}
                        <span className="text-xs whitespace-nowrap">Age {student.age || 'unknown'}</span>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}

MultiSelectStudent.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.array,
  className: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  errorMessage: PropTypes.string,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    dates: PropTypes.array
  })
}

export default MultiSelectStudent
